/**
 * useBillingUsage Hook
 * Fetch current usage metrics (SKU count, SmartImport runs) using React Query
 *
 * Use this hook when you need:
 * - Current usage vs limits
 * - Usage percentages (for progress bars)
 * - Grace period status
 *
 * Stale time: 1 minute (usage updates frequently)
 *
 * NOTE: Backend currently returns stub data (just skuCount as number).
 * This hook transforms it into the full UsageMetrics format expected by UI.
 * When backend implements full usage tracking, this will seamlessly work.
 */

import { useQuery } from '@tanstack/react-query'
import { billingService } from '../services/billingService'
import type { UsageMetrics, SkuUsage, SmartImportUsage } from '../types/billing.types'

/**
 * Backend stub response format (TODO: will be replaced with full format)
 */
interface BackendUsageStub {
  skuCount: number | SkuUsage
  readsToday?: number
  writesToday?: number
  billingOwnerId?: string
  smartimportRuns?: SmartImportUsage
}

/**
 * Default SKU limit for stub data (Growth plan default)
 */
const DEFAULT_SKU_LIMIT = 2500

/**
 * Transform backend response to UsageMetrics format
 * Handles both stub format (number) and full format (object)
 */
function transformUsageResponse(data: BackendUsageStub): UsageMetrics {
  // If skuCount is already in full format, use it
  const skuCount: SkuUsage = typeof data.skuCount === 'object' && data.skuCount !== null
    ? data.skuCount
    : {
        current: typeof data.skuCount === 'number' ? data.skuCount : 0,
        limit: DEFAULT_SKU_LIMIT,
        percentage: typeof data.skuCount === 'number'
          ? Math.min(100, Math.round((data.skuCount / DEFAULT_SKU_LIMIT) * 100))
          : 0,
        inGracePeriod: false
      }

  // If smartimportRuns is already provided, use it; otherwise create stub
  const smartimportRuns: SmartImportUsage = data.smartimportRuns ?? {
    current: 0,
    limit: null,
    percentage: 0,
    resetsOn: getNextMonthFirstDay(),
    unlimited: true
  }

  return { skuCount, smartimportRuns }
}

/**
 * Get ISO date string for first day of next month
 */
function getNextMonthFirstDay(): string {
  const now = new Date()
  const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1)
  return nextMonth.toISOString().split('T')[0]
}

export function useBillingUsage(options?: { enabled?: boolean }) {
  return useQuery<UsageMetrics>({
    queryKey: ['billing', 'usage'],
    queryFn: async () => {
      const response = await billingService.getUsage()

      if (!response.success || !response.data) {
        throw new Error(response.message || 'Failed to fetch usage')
      }

      // Transform stub response to full UsageMetrics format
      return transformUsageResponse(response.data as unknown as BackendUsageStub)
    },
    staleTime: 1000 * 60, // 1 minute
    retry: 2,
    enabled: options?.enabled ?? true
  })
}
